import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
   ComposedChart
} from 'recharts';
import apiService from '../services/api';
import './InsightsDashboard.css';
import PropTypes from 'prop-types';


// Custom tick component with tooltip for truncated labels
const CustomAxisTick = ({ x, y, payload, maxLength = 15 }) => {
  const text = payload.value || '';
  const displayText = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{text}</title>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        fontSize={12}
        transform="rotate(-45)"
      >
        {displayText}
      </text>
    </g>
  );
};
CustomAxisTick.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ])
  }).isRequired,
  maxLength: PropTypes.number
};


function InsightsDashboard() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  
  // Filter states
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  
  // Analytics data states
  const [departmentPerformance, setDepartmentPerformance] = useState([]);
  const [rolePerformance, setRolePerformance] = useState([]);
  const [categoryPerformance, setCategoryPerformance] = useState([]);
  const [ratingComparison, setRatingComparison] = useState([]);
  const [cycleTrends, setCycleTrends] = useState([]);
  const [managerEffectiveness, setManagerEffectiveness] = useState([]);
  const [completionRates, setCompletionRates] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [weightageImpact, setWeightageImpact] = useState([]);
  const [departmentHeatmap, setDepartmentHeatmap] = useState([]);
  
  // Summary KPIs
  const [summaryKPIs, setSummaryKPIs] = useState({
    totalDepartments: 0,
    totalRoles: 0,
    avgCI: 0,
    completionRate: 0
  });

  useEffect(() => {
    document.title = 'CR Capability Index - Insights Dashboard';
  }, []);

  // Fetch metadata (cycles, departments) - roles will come from analytics data
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [cyclesRes, deptRes] = await Promise.all([
          apiService.fetchData('metadata?type=cycles'),
          apiService.fetchData('metadata?type=departments')
        ]);

        if (cyclesRes?.status === 'success' && cyclesRes.data.length > 0) {
          const sortedCycles = [...cyclesRes.data].sort((a, b) => (b.CycleID || 0) - (a.CycleID || 0));
          setAvailableCycles(sortedCycles);
          setSelectedCycle(sortedCycles[0]);
        }

        if (deptRes?.status === 'success') {
          setDepartments(deptRes.data || []);
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
      }
    };

    fetchMetadata();
  }, []);

  // Fetch all analytics when filters change
  useEffect(() => {
    if (selectedCycle) {
      fetchAllAnalytics();
    }
  }, [selectedCycle, selectedDepartment, selectedRole, selectedManager]);


  const setDataIfSuccess = (response, setter, defaultValue = []) => {
  if (response?.status === 'success') {
    setter(response.data || defaultValue);
    return response.data || defaultValue;
  }
  return null;
};

  // const fetchAllAnalytics = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const cycleId = selectedCycle?.CycleID;
  //     if (!cycleId) return;

  //     const baseParams = `cycle_id=${cycleId}`;
  //     const deptParam = selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : '';
  //     const roleParam = selectedRole ? `&role=${encodeURIComponent(selectedRole)}` : '';
  //     const managerParam = selectedManager ? `&manager_email=${encodeURIComponent(selectedManager)}` : '';
  //     const filterParams = `${baseParams}${deptParam}${roleParam}${managerParam}`;

  //       // Fetch all analytics in parallel
  //       const [
  //         deptPerf,
  //         rolePerf,
  //         catPerf,
  //         ratingComp,
  //         cycleTrend,
  //         managerEff,
  //         completion,
  //         topPerf,
  //         weightage,
  //         heatmap
  //       ] = await Promise.all([
  //         apiService.fetchData(`analytics?insight=department_performance&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=role_performance&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=category_performance&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=rating_comparison&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=cycle_trends&cycles_count=5`),
  //         apiService.fetchData(`analytics?insight=manager_effectiveness&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=completion_rates&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=top_performers&${filterParams}&limit=10`),
  //         apiService.fetchData(`analytics?insight=weightage_impact&${filterParams}`),
  //         apiService.fetchData(`analytics?insight=department_heatmap&${filterParams}`)
  //       ]);

  //     // Set data
  //     if (deptPerf?.status === 'success') setDepartmentPerformance(deptPerf.data || []);
  //     if (rolePerf?.status === 'success') {
  //       const roleData = rolePerf.data || [];
  //       setRolePerformance(roleData);
  //       // Extract unique roles from the analytics data for the dropdown
  //       const uniqueRoles = [...new Set(roleData.map(item => item.Role).filter(Boolean))];
  //       const rolesForDropdown = uniqueRoles.map(role => ({ Role: role, RoleID: role }));
  //       setRoles(rolesForDropdown);
  //     }
  //     if (catPerf?.status === 'success') setCategoryPerformance(catPerf.data || []);
  //     if (ratingComp?.status === 'success') setRatingComparison(ratingComp.data || []);
  //     if (cycleTrend?.status === 'success') setCycleTrends(cycleTrend.data || []);
  //     if (managerEff?.status === 'success') setManagerEffectiveness(managerEff.data || []);
  //     if (completion?.status === 'success') setCompletionRates(completion.data || []);
  //     if (topPerf?.status === 'success') setTopPerformers(topPerf.data || []);
  //     if (weightage?.status === 'success') setWeightageImpact(weightage.data || []);
  //     if (heatmap?.status === 'success') setDepartmentHeatmap(heatmap.data || []);

  //     // Calculate summary KPIs
  //     if (deptPerf?.data) {
  //       const avgCI = deptPerf.data.reduce((sum, d) => sum + (parseFloat(d.AvgCI) || 0), 0) / (deptPerf.data.length || 1);
  //       const totalDepts = deptPerf.data.length;
  //       const totalRoles = rolePerf?.data?.length || 0; // This is the actual count of role records
  //       const avgCompletion = completion?.data?.reduce((sum, c) => sum + (parseFloat(c.FullCompletionRate) || 0), 0) / (completion.data.length || 1) || 0;

  //       setSummaryKPIs({
  //         totalDepartments: totalDepts,
  //         totalRoles: totalRoles, // This represents number of role records in analytics
  //         avgCI: parseFloat(avgCI.toFixed(2)),
  //         completionRate: parseFloat(avgCompletion.toFixed(2))
  //       });
  //     }

  //   } catch (err) {
  //     console.error('Error fetching analytics:', err);
  //     setError(err.message || 'Failed to load analytics data');
  //   } finally {
  //     setLoading(false);
  //   }
  // };
const fetchAllAnalytics = async () => {
  try {
    setLoading(true);
    setError(null);

    const cycleId = selectedCycle?.CycleID;
    if (!cycleId) return;

    const baseParams = `cycle_id=${cycleId}`;
    const deptParam = selectedDepartment
      ? `&department=${encodeURIComponent(selectedDepartment)}`
      : '';
    const roleParam = selectedRole
      ? `&role=${encodeURIComponent(selectedRole)}`
      : '';
    const managerParam = selectedManager
      ? `&manager_email=${encodeURIComponent(selectedManager)}`
      : '';

    const filterParams = `${baseParams}${deptParam}${roleParam}${managerParam}`;

    // Fetch all analytics in parallel
    const [
      deptPerf,
      rolePerf,
      catPerf,
      ratingComp,
      cycleTrend,
      managerEff,
      completion,
      topPerf,
      weightage,
      heatmap
    ] = await Promise.all([
      apiService.fetchData(`analytics?insight=department_performance&${filterParams}`),
      apiService.fetchData(`analytics?insight=role_performance&${filterParams}`),
      apiService.fetchData(`analytics?insight=category_performance&${filterParams}`),
      apiService.fetchData(`analytics?insight=rating_comparison&${filterParams}`),
      apiService.fetchData(`analytics?insight=cycle_trends&cycles_count=5`),
      apiService.fetchData(`analytics?insight=manager_effectiveness&${filterParams}`),
      apiService.fetchData(`analytics?insight=completion_rates&${filterParams}`),
      apiService.fetchData(`analytics?insight=top_performers&${filterParams}&limit=10`),
      apiService.fetchData(`analytics?insight=weightage_impact&${filterParams}`),
      apiService.fetchData(`analytics?insight=department_heatmap&${filterParams}`)
    ]);

    // Set analytics data safely
    const deptData = setDataIfSuccess(deptPerf, setDepartmentPerformance);
    const roleData = setDataIfSuccess(rolePerf, setRolePerformance);
    setDataIfSuccess(catPerf, setCategoryPerformance);
    setDataIfSuccess(ratingComp, setRatingComparison);
    setDataIfSuccess(cycleTrend, setCycleTrends);
    setDataIfSuccess(managerEff, setManagerEffectiveness);
    const completionData = setDataIfSuccess(completion, setCompletionRates);
    setDataIfSuccess(topPerf, setTopPerformers);
    setDataIfSuccess(weightage, setWeightageImpact);
    setDataIfSuccess(heatmap, setDepartmentHeatmap);

    // Populate role dropdown (unchanged behavior)
    if (roleData) {
      const uniqueRoles = [...new Set(roleData.map(item => item.Role).filter(Boolean))];
      const rolesForDropdown = uniqueRoles.map(role => ({
        Role: role,
        RoleID: role
      }));
      setRoles(rolesForDropdown);
    }

    // Calculate summary KPIs (same logic)
    if (deptData) {
      const avgCI =
        deptData.reduce(
          (sum, d) => sum + (parseFloat(d.AvgCI) || 0),
          0
        ) / (deptData.length || 1);

      const totalDepts = deptData.length;
      const totalRoles = roleData?.length || 0;

      const avgCompletion =
        completionData?.reduce(
          (sum, c) => sum + (parseFloat(c.FullCompletionRate) || 0),
          0
        ) / (completionData?.length || 1) || 0;

      setSummaryKPIs({
        totalDepartments: totalDepts,
        totalRoles,
        avgCI: parseFloat(avgCI.toFixed(2)),
        completionRate: parseFloat(avgCompletion.toFixed(2))
      });
    }

  } catch (err) {
    console.error('Error fetching analytics:', err);
    setError(err.message || 'Failed to load analytics data');
  } finally {
    setLoading(false);
  }
};

  const handleCycleChange = (event) => {
    const cycleId = parseInt(event.target.value);
    const selected = availableCycles.find(cycle => cycle.CycleID === cycleId);
    setSelectedCycle(selected);
  };

  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#06b6d4', '#ef4444', '#84cc16', '#f59e0b', '#6366f1'];

  // Prepare heatmap data
  const prepareHeatmapData = () => {
    const deptMap = {};
    const categories = [...new Set(departmentHeatmap.map(h => h.CategoryName))];
    
    departmentHeatmap.forEach(item => {
      if (!deptMap[item.DepartmentName]) {
        deptMap[item.DepartmentName] = {};
      }
      deptMap[item.DepartmentName][item.CategoryName] = parseFloat(item.AvgCIScore) || 0;
    });

    return Object.keys(deptMap).map(dept => {
      const data = { department: dept };
      categories.forEach(cat => {
        data[cat] = deptMap[dept][cat] || 0;
      });
      return data;
    });
  };

  const heatmapData = prepareHeatmapData();

  return (
    <div className="insights-dashboard-container">
      {/* Header */}
      <div className="insights-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        
        <div className="header-content">
          <h1>Advanced Insights Dashboard</h1>
          <p>Comprehensive analytics and performance insights</p>
        </div>

        <div className="header-actions">
          <div className="filter-group">
            <label>Assessment Cycle:</label>
            <select
              value={selectedCycle?.CycleID || ''}
              onChange={handleCycleChange}
              disabled={loading}
            >
              {availableCycles.map((cycle) => (
                <option key={cycle.CycleID} value={cycle.CycleID}>
                  {cycle.Name || cycle.CycleID}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-item">
          <label>Department:</label>
          <select
            value={selectedDepartment || ''}
            onChange={(e) => setSelectedDepartment(e.target.value || null)}
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept.DepartmentID || dept.DepartmentName} value={dept.DepartmentName}>
                {dept.DepartmentName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          {/* <label>Role: ({roles.length} with data)</label> */}
          <select
            value={selectedRole || ''}
            onChange={(e) => setSelectedRole(e.target.value || null)}
          >
            <option value="">All Roles</option>
            {roles.length > 0 ? roles.map((role, index) => (
              <option key={role.RoleID || role.Role || index} value={role.Role}>
                {role.Role || `Role ${index + 1}`}
              </option>
            )) : (
              <option disabled>No roles available</option>
            )}
          </select>
        </div>

        <button 
          className="clear-filters-btn"
          onClick={() => {
            setSelectedDepartment(null);
            setSelectedRole(null);
            setSelectedManager(null);
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load Analytics</h3>
          <p>{error}</p>
          <button onClick={fetchAllAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {/* Summary KPIs */}
      {!loading && !error && (
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon">🏢</div>
            <div className="kpi-content">
              <div className="kpi-label">Departments</div>
              <div className="kpi-value">{summaryKPIs.totalDepartments}</div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <div className="kpi-label">Job Roles</div>
              <div className="kpi-value">{summaryKPIs.totalRoles}</div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon">📊</div>
            <div className="kpi-content">
              <div className="kpi-label">Average CI</div>
              <div className="kpi-value">{summaryKPIs.avgCI}</div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon">✅</div>
            <div className="kpi-content">
              <div className="kpi-label">Completion Rate</div>
              <div className="kpi-value">{summaryKPIs.completionRate}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {!loading && !error && (
        <div className="charts-section">
          {/* Department Performance */}
          <div className="chart-card">
            <h3>Department Performance</h3>
            <p className="chart-subtitle">Average CI scores by department</p>
            {departmentPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={departmentPerformance} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="DepartmentName"
                    height={100}
                    tick={<CustomAxisTick maxLength={15} />}
                    interval={0}
                    label={{ value: 'Department', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    domain={[0, 5]}
                    label={{ value: 'CI Score / Members', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="AvgCI" fill="#3b82f6" name="Avg CI Score" />
                  <Bar dataKey="Members" fill="#8b5cf6" name="Members" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No department data available</div>
            )}
          </div>

          {/* Role Performance */}
          <div className="chart-card">
            <h3>Role Performance</h3>
            <p className="chart-subtitle">CI scores across different roles</p>
            {rolePerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={rolePerformance} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="Role"
                    height={100}
                    tick={<CustomAxisTick maxLength={15} />}
                    interval={0}
                    label={{ value: 'Role', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    domain={[0, 5]}
                    label={{ value: 'Rating Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="AvgCIScore" fill="#22c55e" name="Avg CI" />
                  <Bar dataKey="AvgSelfRating" fill="#06b6d4" name="Avg Self Rating" />
                  <Bar dataKey="AvgManagerRating" fill="#f97316" name="Avg Manager Rating" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No role data available</div>
            )}
          </div>

          {/* Category Performance */}
          <div className="chart-card">
            <h3>Category Performance</h3>
            <p className="chart-subtitle">Performance across capability categories</p>
            {categoryPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={categoryPerformance}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="CategoryName" />
                  <PolarRadiusAxis domain={[0, 5]} />
                  <Radar name="Avg CI" dataKey="AvgCIScore" stroke="#a855f7" fill="#a855f7" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No category data available</div>
            )}
          </div>

          {/* Cycle Trends */}
          {/* <div className="chart-card">
            <h3>Cycle Trends</h3>
            <p className="chart-subtitle">Performance trends across assessment cycles</p>
            {cycleTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={cycleTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="CycleName" />
                  <YAxis domain={[0, 5]} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="AvgCIScore" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                  <Line type="monotone" dataKey="AvgSelfRating" stroke="#06b6d4" strokeWidth={2} />
                  <Line type="monotone" dataKey="AvgManagerRating" stroke="#f97316" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No cycle trend data available</div>
            )}
          </div> */}

          {/* Manager Effectiveness */}
          <div className="chart-card">
            <h3>Manager Effectiveness</h3>
            <p className="chart-subtitle">Manager rating patterns and team performance</p>
            {managerEffectiveness.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={managerEffectiveness} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ManagerEmail"
                    height={100}
                    tick={<CustomAxisTick maxLength={12} />}
                    interval={0}
                    label={{ value: 'Manager Email', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    yAxisId="left"
                    label={{ value: 'Team Size', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    label={{ value: 'CI Score / Rating Gap', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar yAxisId="left" dataKey="TeamSize" fill="#8b5cf6" name="Team Size" />
                  <Line yAxisId="right" type="monotone" dataKey="AvgTeamCI" stroke="#22c55e" strokeWidth={2} name="Avg Team CI" />
                  <Line yAxisId="right" type="monotone" dataKey="AvgRatingGap" stroke="#ef4444" strokeWidth={2} name="Rating Gap" />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No manager effectiveness data available</div>
            )}
          </div>

          {/* Completion Rates */}
          <div className="chart-card">
            <h3>Assessment Completion Rates</h3>
            <p className="chart-subtitle">Completion rates by department and role</p>
            {completionRates.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={completionRates.map(item => ({
                  ...item,
                  displayLabel: `${item.DepartmentName} - ${item.Role}`,
                  SelfCompletionRate: parseFloat((parseFloat(item.SelfCompletionRate) || 0).toFixed(2)),
                  ManagerCompletionRate: parseFloat((parseFloat(item.ManagerCompletionRate) || 0).toFixed(2)),
                  FullCompletionRate: parseFloat((parseFloat(item.FullCompletionRate) || 0).toFixed(2))
                }))} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="displayLabel"
                    height={100}
                    tick={<CustomAxisTick maxLength={15} />}
                    interval={0}
                    label={{ value: 'Department - Role', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    label={{ value: 'Completion Rate (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      `${value}%`,
                      name === 'Self Completion %' ? 'Self Assessments' :
                      name === 'Manager Completion %' ? 'Manager Assessments' :
                      'Full Completion'
                    ]}
                    labelFormatter={(label) => `Department & Role: ${label}`}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="SelfCompletionRate" fill="#06b6d4" name="Self Completion %" />
                  <Bar dataKey="ManagerCompletionRate" fill="#f97316" name="Manager Completion %" />
                  <Bar dataKey="FullCompletionRate" fill="#22c55e" name="Full Completion %" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No completion rate data available</div>
            )}
          </div>

          {/* Top Performers */}
          <div className="chart-card">
            <h3>Top Performers</h3>
            <p className="chart-subtitle">Highest performing employees</p>
            {topPerformers.length > 0 ? (
              <div className="performers-table">
                <table>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Role</th>
                      <th>Overall CI</th>
                      <th>Self Rating</th>
                      <th>Manager Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPerformers.map((performer, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{performer.Email}</td>
                        <td>{performer.DepartmentName}</td>
                        <td>{performer.Role}</td>
                        <td><strong>{parseFloat(parseFloat(performer.OverallCI) || 0).toFixed(2)}</strong></td>
                        <td>{performer.SelfAverageRating ? parseFloat(parseFloat(performer.SelfAverageRating) || 0).toFixed(2) : 'N/A'}</td>
                        <td>{performer.ManagerAverageRating ? parseFloat(parseFloat(performer.ManagerAverageRating) || 0).toFixed(2) : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-chart">No top performers data available</div>
            )}
          </div>

          {/* Weightage Impact */}
          {/* <div className="chart-card">
            <h3>Weightage Impact Analysis</h3>
            <p className="chart-subtitle">Weighted vs unweighted category scores</p>
            {weightageImpact.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={weightageImpact} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="CategoryName" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    label={{ value: 'Category', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <YAxis 
                    domain={[0, 5]}
                    label={{ value: 'CI Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#666' } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="WeightedAvgCI" fill="#a855f7" name="Weighted Avg CI" />
                  <Bar dataKey="UnweightedAvgCI" fill="#ec4899" name="Unweighted Avg CI" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No weightage impact data available</div>
            )}
          </div> */}

          {/* Department Heatmap */}
          {heatmapData.length > 0 && (
            <div className="chart-card full-width">
              <h3>Department vs Category Heatmap</h3>
              <p className="chart-subtitle">Performance matrix across departments and categories</p>
              <div className="heatmap-container">
                <table className="heatmap-table">
                  <thead>
                    <tr>
                      <th>Department</th>
                      {[...new Set(departmentHeatmap.map(h => h.CategoryName))].map(cat => (
                        <th key={cat}>{cat}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="heatmap-dept">{row.department}</td>
                        {[...new Set(departmentHeatmap.map(h => h.CategoryName))].map(cat => {
                          const value = row[cat] || 0;
                          const intensity = Math.min(value / 5, 1);
                          return (
                            <td 
                              key={cat}
                              className="heatmap-cell"
                              style={{
                                backgroundColor: `rgba(59, 130, 246, ${intensity})`,
                                color: intensity > 0.5 ? 'white' : '#1e293b'
                              }}
                            >
                              {value.toFixed(2)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InsightsDashboard;

